// (2026-04-22, Jason Cox) Device Status page. Shows a traffic-light list of
// SmartBoxes derived from the existing Smart backend md/v1/listSmartBox
// endpoint -- no backend changes. Status buckets:
//   Green  -- status=1 AND rfidCount>0  (online, tags present)
//   Orange -- status=1 AND rfidCount=0  (online, no tags)
//   Grey   -- status=0                  (offline)
//
// (2026-04-23) Uses the backend md_smartbox.status / online fields --
// same signal the Vue admin's workcenter view uses -- so the PWA is
// consistent with what field staff see in the admin. readerAlert is
// still shown in the per-row detail expansion for reference but
// doesn't drive its own bucket.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import type { SmartboxStatusRow } from '../services/unifiedApiService';
import { SignalIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

type StatusColor = 'green' | 'orange' | 'grey';

// (2026-04-23) Backend may return dataAt either as a raw unix-ts integer
// or as a pre-formatted user-local date string (SmartBox.php overwrites
// the int with TimeTools::getUserDateTime() output). Keep a parser for
// the expand-row display.
function parseTimestampSeconds(value: any): number {
  if (typeof value === 'number' && value > 0) return value;
  if (typeof value === 'string' && value.length > 0) {
    const ms = Date.parse(value);
    if (!isNaN(ms)) return Math.floor(ms / 1000);
  }
  return 0;
}

function deriveStatus(row: SmartboxStatusRow): StatusColor {
  // (2026-04-23) Prefer `status` over `online`. V3's scan handler sets
  // md_smartbox.status=1 on every packet (see apidatav3.php:506).
  // `online` is a commissioning flag set when the smartbox is deployed
  // and can be 0 for live boxes -- e.g. SB093 is status=1, online=0
  // while actively scanning. Mirrors the Vue admin's workcenter list
  // at BaseWorkCenter.php:120 + WorkCenter/index.vue:66 which drives
  // off w.status (set by the same code path).
  const isOnline = ((row.status ?? row.online) === 1);
  if (!isOnline) return 'grey';
  return (row.rfidCount || 0) > 0 ? 'green' : 'orange';
}

const STATUS_LABELS: Record<StatusColor, string> = {
  green: 'Online',
  orange: 'Online (no tags)',
  grey: 'Offline',
};

const STATUS_CLASSES: Record<StatusColor, string> = {
  green: 'bg-green-100 text-green-800 border-green-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  grey: 'bg-secondary-200 text-secondary-700 border-secondary-300',
};

const STATUS_DOT: Record<StatusColor, string> = {
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  grey: 'bg-secondary-400',
};

function formatTimestamp(value: any): string {
  const ts = parseTimestampSeconds(value);
  if (!ts) return typeof value === 'string' && value ? value : '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString();
}

function timeSince(value: any): string {
  const ts = parseTimestampSeconds(value);
  if (!ts) return 'never';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const DeviceStatus: React.FC = () => {
  const { user } = useAuth();
  const api = useUnifiedApi();

  const [rows, setRows] = useState<SmartboxStatusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number | null>(null);

  // Filters
  const [nameFilter, setNameFilter] = useState('');
  const [wNameFilter, setWNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusColor>('all');

  // Pagination: fetch up to 500 rows in one call so summary counts reflect
  // the whole scope, then paginate client-side (50 per UI page).
  const [uiPage, setUiPage] = useState(1);
  const uiPageSize = 50;
  const FETCH_PAGE_SIZE = 500;
  const [totalRowsServer, setTotalRowsServer] = useState(0);

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!api || !api.getSmartboxStatusList) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch a large page to cover the whole user scope in one call.
      // Summary counts below are derived from the full fetched set, not
      // just a visible UI page. Status filter is client-side.
      const res = await api.getSmartboxStatusList({
        page: 1,
        pageSize: FETCH_PAGE_SIZE,
        name: nameFilter || undefined,
        wName: wNameFilter || undefined,
      });
      setRows(res.data);
      setTotalRowsServer(res.total);
      setUiPage(1);
      setLastFetched(Date.now());
    } catch (e: any) {
      setError(e?.message || 'Failed to load device status');
    } finally {
      setLoading(false);
    }
  }, [api, nameFilter, wNameFilter]);

  // Re-fetch when the search filters change (triggered by handleSearch).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSearch = () => {
    setUiPage(1);
    load();
  };

  // Sorted + filtered full set (basis for summary counts AND the paginated table).
  const filteredSorted = useMemo(() => {
    const filtered = statusFilter === 'all'
      ? rows
      : rows.filter(r => deriveStatus(r) === statusFilter);
    // Sort: red (alert) first, then green, orange, grey. Within each
    // bucket, most-recent dataAt first.
    const priority: Record<StatusColor, number> = { green: 0, orange: 1, grey: 2 };
    return [...filtered].sort((a, b) => {
      const pa = priority[deriveStatus(a)];
      const pb = priority[deriveStatus(b)];
      if (pa !== pb) return pa - pb;
      return parseTimestampSeconds(b.dataAt) - parseTimestampSeconds(a.dataAt);
    });
  }, [rows, statusFilter]);

  // Quick counts for the summary strip -- derived from ALL fetched rows,
  // not just the currently visible UI page.
  const counts = useMemo(() => {
    const c: Record<StatusColor, number> = { green: 0, orange: 0, grey: 0 };
    rows.forEach(r => { c[deriveStatus(r)]++; });
    return c;
  }, [rows]);

  // Slice for the table: client-side pagination over filteredSorted.
  const pageSlice = useMemo(() => {
    const start = (uiPage - 1) * uiPageSize;
    return filteredSorted.slice(start, start + uiPageSize);
  }, [filteredSorted, uiPage]);
  const totalUiPages = Math.max(1, Math.ceil(filteredSorted.length / uiPageSize));
  const fetchTruncated = totalRowsServer > rows.length;

  if (user?.system !== 'smart') {
    return (
      <Layout>
        <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary-600">Device Status is available for Smart users only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-secondary-900 sm:text-3xl sm:truncate flex items-center">
                <SignalIcon className="h-8 w-8 mr-2 text-primary-600" />
                Device Status
              </h2>
              <p className="mt-1 text-sm text-secondary-600">
                SmartBox online/offline state as reported by the backend (same signal
                rebound-smart's admin uses). Green = online with tags. Orange = online
                but empty. Grey = offline.
              </p>
            </div>
            <div className="mt-3 md:mt-0 flex items-center space-x-2">
              {lastFetched && (
                <span className="text-xs text-secondary-500">
                  Last refreshed: {new Date(lastFetched).toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={load}
                disabled={loading}
                className="btn-secondary flex items-center"
                title="Refresh"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {(['green', 'orange', 'grey'] as StatusColor[]).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
                className={`card p-3 flex items-center justify-between text-left transition-all ${
                  statusFilter === s ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div>
                  <div className="text-xs text-secondary-500">{STATUS_LABELS[s]}</div>
                  <div className="text-2xl font-bold text-secondary-900">{counts[s]}</div>
                </div>
                <div className={`h-6 w-6 rounded-full ${STATUS_DOT[s]}`} />
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-secondary-700">SmartBox name / code</label>
                  <input
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="input mt-1 w-full"
                    placeholder="e.g. SB035"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700">Workcenter</label>
                  <input
                    value={wNameFilter}
                    onChange={(e) => setWNameFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="input mt-1 w-full"
                    placeholder="e.g. Martin"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="input mt-1 w-full"
                  >
                    <option value="all">All</option>
                    <option value="green">Online</option>
                    <option value="orange">Online (no tags)</option>
                    <option value="grey">Offline</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md p-3 bg-red-50 text-red-800 text-sm mb-4">{error}</div>
          )}

          {/* Results table */}
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 text-sm">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700 w-12">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">SmartBox</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Workcenter</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Tags</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Last packet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {loading && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-secondary-500">Loading...</td></tr>
                )}
                {!loading && pageSlice.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-secondary-500">No SmartBoxes match.</td></tr>
                )}
                {!loading && pageSlice.map(row => {
                  const color = deriveStatus(row);
                  const isExpanded = expandedId === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className="hover:bg-secondary-50 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : row.id)}
                      >
                        <td className="px-3 py-2">
                          <span
                            className={`inline-block h-3 w-3 rounded-full ${STATUS_DOT[color]}`}
                            title={STATUS_LABELS[color]}
                          />
                        </td>
                        <td className="px-3 py-2">
                          {/* (2026-04-23) Collapse code + name to a single value,
                              preferring whichever has no spaces. Both fields are
                              often duplicates; when they differ, `code` is usually
                              the no-space canonical form. */}
                          <div className="font-mono text-xs text-secondary-900">
                            {(() => {
                              const code = row.code || '';
                              const name = row.name || '';
                              if (code && !code.includes(' ')) return code;
                              if (name && !name.includes(' ')) return name;
                              return code || name || '—';
                            })()}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-secondary-700">{row.wName || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{row.rfidCount ?? 0}</td>
                        <td className="px-3 py-2 text-secondary-600">{timeSince(row.dataAt)}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-secondary-50">
                          <td colSpan={5} className="px-3 py-3">
                            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              <div>
                                <dt className="text-secondary-500">SmartBox ID</dt>
                                <dd className="font-mono text-secondary-900">{row.id}</dd>
                              </div>
                              <div>
                                <dt className="text-secondary-500">Last packet at</dt>
                                <dd className="text-secondary-900">{formatTimestamp(row.dataAt)}</dd>
                              </div>
                              <div>
                                <dt className="text-secondary-500">Tag count in last packet</dt>
                                <dd className="text-secondary-900">{row.rfidCount ?? 0}</dd>
                              </div>
                              <div>
                                <dt className="text-secondary-500">Server flags</dt>
                                <dd className="text-secondary-900 text-xs">
                                  status={row.status ?? '—'} / online={row.online ?? '—'} / readerAlert={row.readerAlert ?? 0}
                                </dd>
                              </div>
                              {row.isError !== undefined && (
                                <div>
                                  <dt className="text-secondary-500">Error flag</dt>
                                  <dd className="text-secondary-900">{row.isError ? 'yes' : 'no'}</dd>
                                </div>
                              )}
                              {row.updateAt !== undefined && row.updateAt > 0 && (
                                <div>
                                  <dt className="text-secondary-500">Last updated</dt>
                                  <dd className="text-secondary-900">{formatTimestamp(row.updateAt)}</dd>
                                </div>
                              )}
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination (client-side over the fetched set) */}
          {totalUiPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="text-secondary-600">
                Page {uiPage} of {totalUiPages} ({filteredSorted.length} visible
                {fetchTruncated && `, ${totalRowsServer} total -- narrow your search for exact counts`})
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setUiPage(p => Math.max(1, p - 1))}
                  disabled={uiPage <= 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setUiPage(p => Math.min(totalUiPages, p + 1))}
                  disabled={uiPage >= totalUiPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DeviceStatus;
